import type { MockContext } from '@mocks/mockFetch';

type MockHandler = (ctx: MockContext) => Promise<Response | null> | Response | null;

const respond = (payload: unknown, init?: ResponseInit) => {
  const headers = new Headers(init?.headers ?? undefined);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('X-Mock-Route', 'signals');

  return new Response(JSON.stringify(payload), {
    ...init,
    status: init?.status ?? 200,
    headers
  });
};

const matchOrgSignalsPath = (url: URL) => {
  const parts = url.pathname.split('/').filter(Boolean);
  const orgsIndex = parts.indexOf('orgs');
  if (orgsIndex < 0 || orgsIndex + 1 >= parts.length) {
    return null;
  }
  const orgId = decodeURIComponent(parts[orgsIndex + 1] ?? '');
  const tailParts = parts.slice(orgsIndex + 2);
  const tail = tailParts.join('/');
  if (tail !== 'signals' && !tail.startsWith('signals/')) {
    return null;
  }
  return { orgId, tail: tailParts.slice(1).join('/') };
};

const TELEMETRY_SITE_ID = 'site-sfo';
const EDGE_DEVICE_ID = 'device-edge-01';
const CORE_DEVICE_ID = 'device-core-01';

const SIGNAL_ROWS = [
  {
    family: 'environment',
    signalKey: 'snmp_env_temp_celsius',
    metricName: 'snmp_env_temp_celsius',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: 'env:ent:1101',
    value: 54,
    observedAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'environment',
    signalKey: 'snmp_env_fan_state',
    metricName: 'snmp_env_fan_state',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: 'env:ent:2101',
    value: 1,
    observedAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'tunnel',
    signalKey: 'snmp_tunnel_oper_up',
    metricName: 'snmp_tunnel_oper_up',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: 'tunnel.12',
    value: 0,
    observedAt: new Date(Date.now() - 90 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'bfd',
    signalKey: 'snmp_bfd_session_up',
    metricName: 'snmp_bfd_session_up',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: 'peer:203.0.113.9',
    value: 0,
    observedAt: new Date(Date.now() - 90 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'bgp',
    signalKey: 'snmp_bgp_peer_state',
    metricName: 'snmp_bgp_peer_state',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: '203.0.113.1',
    value: 3,
    observedAt: new Date(Date.now() - 60 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'device_health',
    signalKey: 'snmp_device_cpu_pct',
    metricName: 'snmp_device_cpu_pct',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: '',
    value: 67,
    observedAt: new Date(Date.now() - 60 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'interface',
    signalKey: 'snmp_if_in_octets_total',
    metricName: 'snmp_if_in_octets_total',
    deviceId: CORE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'interface',
    targetId: `ref:inventory.interface:${CORE_DEVICE_ID}#Te1/0/3`,
    instance: '',
    value: 125000000,
    observedAt: new Date(Date.now() - 45 * 1000).toISOString(),
    attributes: { targetLabel: 'Te1/0/3 @ core-sw-01' }
  },
  {
    family: 'stp',
    signalKey: 'snmp_stp_top_changes_total',
    metricName: 'snmp_stp_top_changes_total',
    deviceId: CORE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${CORE_DEVICE_ID}`,
    instance: '',
    value: 2,
    observedAt: new Date(Date.now() - 45 * 1000).toISOString(),
    attributes: { targetLabel: 'core-sw-01' }
  },
  {
    family: 'poe',
    signalKey: 'snmp_poe_main_pse_power_watts',
    metricName: 'snmp_poe_main_pse_power_watts',
    deviceId: CORE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${CORE_DEVICE_ID}`,
    instance: 'pse:1',
    value: 487,
    observedAt: new Date(Date.now() - 45 * 1000).toISOString(),
    attributes: { targetLabel: 'core-sw-01 PSE 1' }
  },
  {
    family: 'firewall_sessions',
    signalKey: 'snmp_pan_session_utilization_pct',
    metricName: 'snmp_pan_session_utilization_pct',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: '',
    value: 42,
    observedAt: new Date(Date.now() - 45 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-fw-01' }
  },
  {
    family: 'redundancy',
    signalKey: 'snmp_if_speed',
    metricName: 'snmp_if_speed',
    deviceId: CORE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'interface',
    targetId: `ref:inventory.interface:${CORE_DEVICE_ID}#Po10`,
    instance: '',
    value: 2000000000,
    observedAt: new Date(Date.now() - 45 * 1000).toISOString(),
    attributes: { targetLabel: 'Po10 @ core-sw-01' }
  }
];

const FEATURE_ROWS = [
  {
    family: 'environment',
    featureKey: 'environment.max_temp_celsius',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: '',
    value: 54,
    updatedAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'tunnel',
    featureKey: 'tunnel.up',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: 'tunnel.12',
    value: 0,
    updatedAt: new Date(Date.now() - 90 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'bfd',
    featureKey: 'bfd.flap_rate_15m',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: 'peer:203.0.113.9',
    value: 4,
    updatedAt: new Date(Date.now() - 90 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'bgp',
    featureKey: 'bgp.peer_established',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: '203.0.113.1',
    value: 0,
    updatedAt: new Date(Date.now() - 60 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'device_health',
    featureKey: 'device.cpu_pct_p95_5m',
    deviceId: EDGE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'node',
    targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
    instance: '',
    value: 72,
    updatedAt: new Date(Date.now() - 60 * 1000).toISOString(),
    attributes: { targetLabel: 'edge-sw-01' }
  },
  {
    family: 'interface',
    featureKey: 'interface.util_pct_p95_5m',
    deviceId: CORE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'interface',
    targetId: `ref:inventory.interface:${CORE_DEVICE_ID}#Te1/0/3`,
    instance: '',
    value: 89,
    updatedAt: new Date(Date.now() - 45 * 1000).toISOString(),
    attributes: { targetLabel: 'Te1/0/3 @ core-sw-01' }
  },
  {
    family: 'redundancy',
    featureKey: 'lag.members_up_ratio',
    deviceId: CORE_DEVICE_ID,
    scopeType: 'site',
    scopeId: TELEMETRY_SITE_ID,
    targetType: 'interface',
    targetId: `ref:inventory.interface:${CORE_DEVICE_ID}#Po10`,
    instance: '',
    value: 0.5,
    updatedAt: new Date(Date.now() - 45 * 1000).toISOString(),
    attributes: { targetLabel: 'Po10 @ core-sw-01' }
  }
];

const handleIncidents: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgSignalsPath(url);
  if (!match || match.tail !== 'incidents') {
    return null;
  }

  const scopeType = url.searchParams.get('scopeType') ?? 'org';
  const scopeId = url.searchParams.get('scopeId') ?? match.orgId;
  const status = (url.searchParams.get('status') ?? 'active').toLowerCase();
  const severity = (url.searchParams.get('severity') ?? '').toLowerCase();
  const subtype = (url.searchParams.get('subtype') ?? '').trim().toLowerCase();
  const limit = Math.max(1, Math.min(200, Number.parseInt(url.searchParams.get('limit') ?? '50', 10) || 50));
  const after = url.searchParams.get('after');
  const before = url.searchParams.get('before');

  const now = Date.now();
  const items = [
    {
      id: 'inc-001',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      subtype: 'interface_congested',
      dedupeKey: 'interface-congested-1',
      status: 'OPEN',
      severity: 'warn',
      conditionKey: 'interface.congested',
      instance: 'default',
      targetType: 'interface',
      targetId: 'ref:inventory.interface:740029df-3057-440b-9cba-4e3bfe96ce56#Te1/0/3',
      openedAt: new Date(now - 15 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 4 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Te1/0/3 @ Dist1.example.com' }
    },
    {
      id: 'inc-002',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      subtype: 'uplink_lag_degraded',
      dedupeKey: 'lag-degraded-1',
      status: 'OPEN',
      severity: 'crit',
      conditionKey: 'lag.degraded',
      instance: 'default',
      targetType: 'interface',
      targetId: 'ref:inventory.interface:740029df-3057-440b-9cba-4e3bfe96ce56#Po10',
      openedAt: new Date(now - 42 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 5 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Po10 @ Dist1.example.com' }
    },
    {
      id: 'inc-003',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: '60eaead0-1367-41c8-851b-ca34abd222d7',
      subtype: 'uplink_lag_degraded',
      dedupeKey: 'lag-degraded-2',
      status: 'CLOSING',
      severity: 'crit',
      conditionKey: 'lag.degraded',
      instance: 'default',
      targetType: 'interface',
      targetId: 'ref:inventory.interface:a138914f-72cc-4cb2-b8c4-accb80d3f34a#Po1',
      openedAt: new Date(now - 90 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 20 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Po1 @ Access1.example.com' }
    },
    {
      id: 'inc-004',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      subtype: 'border_bgp_peer_down',
      dedupeKey: 'bgp-peer-down-1',
      status: 'OPEN',
      severity: 'crit',
      conditionKey: 'bgp.neighbor_down',
      instance: '203.0.113.1',
      targetType: 'node',
      targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
      openedAt: new Date(now - 18 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 2 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Border1.example.com' }
    },
    {
      id: 'inc-005',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      subtype: 'border_routing_unstable',
      dedupeKey: 'bgp-unstable-1',
      status: 'OPEN',
      severity: 'warn',
      conditionKey: 'bgp.unstable',
      instance: '',
      targetType: 'node',
      targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
      openedAt: new Date(now - 35 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 3 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Border1.example.com' }
    },
    {
      id: 'inc-006',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      subtype: 'device_health_degraded',
      dedupeKey: 'device-health-1',
      status: 'OPEN',
      severity: 'warn',
      conditionKey: 'device.memory_pressure',
      instance: '',
      targetType: 'node',
      targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
      openedAt: new Date(now - 24 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 4 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Border1.example.com', condition_key: 'device.memory_pressure' }
    },
    {
      id: 'inc-007',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      subtype: 'site_tunnel_connectivity_degraded',
      dedupeKey: 'tunnel-connectivity-1',
      status: 'OPEN',
      severity: 'crit',
      conditionKey: 'tunnel.down',
      instance: 'tunnel.12',
      targetType: 'node',
      targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
      openedAt: new Date(now - 28 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 3 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Border1.example.com', condition_key: 'tunnel.down' }
    },
    {
      id: 'inc-008',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      subtype: 'site_bfd_liveness_degraded',
      dedupeKey: 'bfd-liveness-1',
      status: 'OPEN',
      severity: 'warn',
      conditionKey: 'bfd.flapping',
      instance: 'peer:203.0.113.9',
      targetType: 'node',
      targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
      openedAt: new Date(now - 21 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 2 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Border1.example.com', condition_key: 'bfd.flapping' }
    },
    {
      id: 'inc-009',
      orgId: match.orgId,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      subtype: 'device_environmental_risk',
      dedupeKey: 'device-environment-1',
      status: 'OPEN',
      severity: 'warn',
      conditionKey: 'device.overheating',
      instance: '',
      targetType: 'node',
      targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
      openedAt: new Date(now - 17 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 2 * 60 * 1000).toISOString(),
      attributes: { targetLabel: 'Border1.example.com', condition_key: 'device.overheating' }
    }
  ]
    .filter((item) => {
      if (scopeType === 'site') {
        return item.scopeType === 'site' && item.scopeId === scopeId;
      }
      return true;
    })
    .filter((item) => {
      switch (status) {
        case 'open':
          return item.status === 'OPEN';
        case 'closing':
          return item.status === 'CLOSING';
        case 'closed':
          return item.status === 'CLOSED';
        case 'any':
          return true;
        case 'active':
        default:
          return item.status !== 'CLOSED';
      }
    })
    .filter((item) => (severity ? item.severity.toLowerCase() === severity : true))
    .filter((item) => (subtype ? item.subtype.toLowerCase().includes(subtype) : true))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));

  const offsetFromToken = (token: string | null): number => {
    if (!token) {
      return 0;
    }
    const parsed = Number.parseInt(token, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  };

  const start = after ? offsetFromToken(after) : before ? Math.max(0, offsetFromToken(before) - limit) : 0;
  const pageItems = items.slice(start, start + limit);
  const next = start + limit < items.length ? String(start + limit) : null;
  const prev = start > 0 ? String(Math.max(0, start - limit)) : null;
  const payload = { items: pageItems, meta: { limit, next, prev, exactTotal: items.length } };
  const etag = `"mock-incidents-${scopeType}-${scopeId}-${status}-${severity}-${subtype}-${start}-${items.length}"`;

  if (request.headers.get('If-None-Match') === etag) {
    return new Response(null, { status: 304, headers: { ETag: etag, 'X-Mock-Route': 'signals' } });
  }

  const links = [
    next ? `</orgs/${encodeURIComponent(match.orgId)}/signals/incidents?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}&after=${encodeURIComponent(next)}>; rel="next"` : null,
    prev ? `</orgs/${encodeURIComponent(match.orgId)}/signals/incidents?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}&before=${encodeURIComponent(prev)}>; rel="prev"` : null
  ]
    .filter(Boolean)
    .join(', ');

  return respond(payload, {
    headers: {
      ETag: etag,
      'Cache-Control': 'private, max-age=15',
      ...(links ? { Link: links } : {})
    }
  });
};

const handleIncidentDetail: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgSignalsPath(url);
  if (!match) {
    return null;
  }

  const m = /^incidents\/([^/]+)$/.exec(match.tail);
  if (!m) {
    return null;
  }
  const incidentId = decodeURIComponent(m[1] ?? '');
  if (!incidentId) {
    return null;
  }

  return respond(
    incidentId === 'inc-002'
      ? {
          id: incidentId,
          title: 'LAG degraded',
          subtype: 'uplink_lag_degraded',
          status: 'open',
          severity: 'critical',
          scopeType: 'site',
          scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
          targetId: 'ref:inventory.interface:740029df-3057-440b-9cba-4e3bfe96ce56#Po10',
          createdAt: new Date(Date.now() - 42 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          description: 'Mock incident detail. In production this will include evidence summary and references.',
          analysis: {
            summary: 'REAP detected that uplink bundle Po10 @ Dist1.example.com is degraded and has lost part of its expected member capacity.',
            impact: 'Reduced uplink redundancy and possible loss of aggregate bandwidth until the missing members recover.',
            rationale: [
              'Incident subtype uplink_lag_degraded indicates the bundle has fewer usable member links than expected.',
              'Topology context narrows the view to the affected bundle, its local members, and the peer-side bundle context.',
              'Telemetry evidence focuses on bundle-member health and member-interface utilization for the affected target.'
            ],
            nextChecks: [
              'Check which local member interfaces are down, suspended, or administratively disabled.',
              'Verify peer-side bundle and LACP state for the same uplink.',
              'Review recent device syslogs for member link transitions, line protocol changes, or LACP events.'
            ]
          },
          topologyIntent: {
            viewId: 'lag_link_members',
            scopeType: 'site',
            scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
            limits: {
              rootNodeId: 'ref:inventory.interface:740029df-3057-440b-9cba-4e3bfe96ce56#Po10',
              hops: 1,
              limitNodes: 40,
              limitEdges: 64
            },
            reason: 'Center on the degraded LAG and show its member interfaces.'
          },
          telemetryIntent: {
            reason: 'Curated mock Grafana evidence for the incident subtype.',
            widgets: [
              {
                id: 'incident-lag-members-target',
                type: 'grafana_panel_iframe',
                title: 'Primary evidence: incident LAG members up ratio',
                bindings: { vars: { site_id: 'cbff025f-e881-4394-b735-eeaaa906187f' } },
                iframeUrl:
                  '/mock-grafana-iframe.html?panelId=14&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.interface%3A740029df-3057-440b-9cba-4e3bfe96ce56%23Po10&from=now-1h&to=now'
              },
              {
                id: 'incident-lag-member-utilization-target',
                type: 'grafana_panel_iframe',
                title: 'Secondary context: affected bundle member interface utilization',
                bindings: {
                  vars: {
                    site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                    target_id: 'ref:inventory.interface:740029df-3057-440b-9cba-4e3bfe96ce56#Po10'
                  }
                },
                iframeUrl:
                  '/mock-grafana-iframe.html?panelId=15&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.interface%3A740029df-3057-440b-9cba-4e3bfe96ce56%23Po10&from=now-1h&to=now'
              }
            ]
          }
        }
      : incidentId === 'inc-004'
        ? {
            id: incidentId,
            title: 'Border BGP peer down',
            subtype: 'border_bgp_peer_down',
            status: 'open',
            severity: 'critical',
            scopeType: 'site',
            scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
            targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
            instance: '203.0.113.1',
            createdAt: new Date(Date.now() - 18 * 60 * 1000).toISOString(),
            updatedAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
            description: 'Mock incident detail. In production this will include evidence summary and references.',
            attachments: [
              {
                id: 'inc-004-att-1',
                name: 'border-rack-snapshot.png',
                mimeType: 'image/png',
                sizeBytes: 2048,
                provider: 'jira',
                providerAttachmentId: '10045',
                sourceUrl: 'https://jira.example/rest/api/3/attachment/content/10045',
                sourceCreatedAt: new Date(Date.now() - 19 * 60 * 1000).toISOString(),
                author: 'alice@example.com',
                normalizationStatus: 'normalized',
                resourceRef: 'file:file-ops-rack-snapshot-png',
                availabilityState: 'preview_ready',
                analysisState: 'ready',
                analysisFamily: 'visual',
                preferredRep: 'visual:v1',
                reps: ['visual:v1', 'object:o_regions']
              },
              {
                id: 'inc-004-att-2',
                name: 'border-capacity-plan.xlsx',
                mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                sizeBytes: 4096,
                provider: 'jira',
                providerAttachmentId: '10046',
                sourceUrl: 'https://jira.example/rest/api/3/attachment/content/10046',
                sourceCreatedAt: new Date(Date.now() - 17 * 60 * 1000).toISOString(),
                author: 'alice@example.com',
                normalizationStatus: 'normalized',
                resourceRef: 'file:file-ops-capacity-workbook-xlsx',
                availabilityState: 'download_only',
                analysisState: 'ready',
                analysisFamily: 'table',
                preferredRep: 'object:o_workbook_manifest',
                reps: ['object:o_workbook_manifest', 'table:t_sheet_capacity']
              },
              {
                id: 'inc-004-att-3',
                name: 'handoff-capture.pcap',
                mimeType: 'application/vnd.tcpdump.pcap',
                sizeBytes: 8192,
                provider: 'jira',
                providerAttachmentId: '10047',
                sourceUrl: 'https://jira.example/rest/api/3/attachment/content/10047',
                sourceCreatedAt: new Date(Date.now() - 16 * 60 * 1000).toISOString(),
                author: 'alice@example.com',
                normalizationStatus: 'normalized',
                resourceRef: 'file:file-ops-branch17-capture-pcap',
                availabilityState: 'none',
                analysisState: 'none',
                analysisFamily: 'packet_capture',
                blockedReason: 'PCAP inspection is not enabled in this phase.'
              },
              {
                id: 'inc-004-att-4',
                name: 'provider-notes.pdf',
                mimeType: 'application/pdf',
                sizeBytes: 182344,
                provider: 'jira',
                providerAttachmentId: '10048',
                sourceUrl: 'https://jira.example/rest/api/3/attachment/content/10048',
                sourceCreatedAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
                author: 'alice@example.com',
                normalizationStatus: 'discovered'
              }
            ],
            analysis: {
              summary: 'REAP detected that border BGP peer 203.0.113.1 on Border1.example.com is down.',
              impact: 'Loss of that external routing adjacency can reduce route diversity or upstream reachability.',
              rationale: [
                'Incident subtype border_bgp_peer_down indicates the peer remained not-established beyond debounce.',
                'Topology context centers on the border device in routing adjacency mode.',
                'Telemetry evidence shows peer established state and recent reset churn on the affected border device.'
              ],
              nextChecks: [
                'Verify remote peer reachability and interface state on the border device.',
                'Check recent BGP FSM transitions, hold timer expiry, or notification messages.',
                'Confirm whether this peer is a critical transit or provider edge adjacency.'
              ]
            },
            topologyIntent: {
              viewId: 'routing_adjacency',
              scopeType: 'site',
              scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
              limits: {
                rootNodeId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                hops: 1,
                limitNodes: 40,
                limitEdges: 64
              },
              reason: 'Center on the border device in routing adjacency context for the affected peer.'
            },
            telemetryIntent: {
              reason: 'Curated mock Grafana evidence for the incident subtype.',
              widgets: [
                {
                  id: 'incident-bgp-peer-established-detail',
                  type: 'grafana_panel_iframe',
                  title: 'Primary evidence: incident BGP peer established state',
                  bindings: {
                    vars: {
                      site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                      target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                    }
                  },
                  iframeUrl:
                    '/mock-grafana-iframe.html?panelId=18&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                },
                {
                  id: 'incident-bgp-reset-churn-detail',
                  type: 'grafana_panel_iframe',
                  title: 'Secondary context: incident BGP reset churn',
                  bindings: {
                    vars: {
                      site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                      target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                    }
                  },
                  iframeUrl:
                    '/mock-grafana-iframe.html?panelId=19&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                }
              ]
            }
          }
        : incidentId === 'inc-005'
          ? {
              id: incidentId,
              title: 'Border routing unstable',
              subtype: 'border_routing_unstable',
              status: 'open',
              severity: 'warning',
              scopeType: 'site',
              scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
              targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
              createdAt: new Date(Date.now() - 35 * 60 * 1000).toISOString(),
              updatedAt: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
              description: 'Mock incident detail. In production this will include evidence summary and references.',
              analysis: {
                summary: 'REAP detected sustained BGP instability on border device Border1.example.com.',
                impact: 'Repeated BGP resets can create intermittent route churn and loss of control-plane stability.',
                rationale: [
                  'Incident subtype border_routing_unstable indicates established-transition churn crossed the configured threshold.',
                  'Telemetry evidence focuses on device-level reset churn and per-peer established state.',
                  'Topology context centers on the affected border device and adjacent routing context.'
                ],
                nextChecks: [
                  'Check whether multiple peers are flapping or the churn is isolated to one provider edge.',
                  'Review device syslogs for BGP notification, hold timer, or transport instability messages.',
                  'Confirm whether the instability aligns with an access, WAN, or maintenance event.'
                ]
              },
              topologyIntent: {
                viewId: 'routing_adjacency',
                scopeType: 'site',
                scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                limits: {
                  rootNodeId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                  hops: 1,
                  limitNodes: 40,
                  limitEdges: 64
                },
                reason: 'Center on the border device in routing adjacency context for routing instability.'
              },
              telemetryIntent: {
                reason: 'Curated mock Grafana evidence for the incident subtype.',
                widgets: [
                  {
                    id: 'incident-bgp-reset-churn-detail',
                    type: 'grafana_panel_iframe',
                    title: 'Primary evidence: incident BGP reset churn',
                    bindings: {
                      vars: {
                        site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                        target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                      }
                    },
                    iframeUrl:
                      '/mock-grafana-iframe.html?panelId=19&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                  },
                  {
                    id: 'incident-bgp-peer-established-detail',
                    type: 'grafana_panel_iframe',
                    title: 'Secondary context: incident BGP peer established state',
                    bindings: {
                      vars: {
                        site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                        target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                      }
                    },
                    iframeUrl:
                      '/mock-grafana-iframe.html?panelId=18&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                  }
                ]
              }
            }
          : incidentId === 'inc-006'
            ? {
                id: incidentId,
                title: 'Device health degraded',
                subtype: 'device_health_degraded',
                status: 'open',
                severity: 'warning',
                scopeType: 'site',
                scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                createdAt: new Date(Date.now() - 24 * 60 * 1000).toISOString(),
                updatedAt: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
                description: 'Mock incident detail. In production this will include evidence summary and references.',
                analysis: {
                  summary: 'REAP detected sustained memory pressure on Border1.example.com.',
                  impact: 'Sustained device resource stress can degrade control-plane responsiveness and amplify other service symptoms on the affected node.',
                  rationale: [
                    'Incident subtype device_health_degraded indicates device CPU-high or memory-pressure conditions persisted long enough to surface a durable device-level incident.',
                    'This device is currently modeled as a memory-pressure-led incident, so memory p95 5m should be the first evidence read.',
                    'Topology context stays device-scoped while telemetry focuses on CPU and memory pressure for the affected node.'
                  ],
                  nextChecks: [
                    'Confirm whether the pressure is isolated to memory or paired with CPU stress before choosing a remediation path.',
                    'Review recent device syslog for resource exhaustion, process instability, or platform warnings on the same node.',
                    'Correlate the timing against nearby routing, interface, or bundle incidents to separate root cause from downstream symptoms.'
                  ]
                },
                topologyIntent: {
                  viewId: 'physical_overview',
                  scopeType: 'site',
                  scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                  limits: {
                    rootNodeId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                    hops: 1,
                    limitNodes: 40,
                    limitEdges: 64
                  },
                  reason: 'Center on the affected device in the physical topology.'
                },
                telemetryIntent: {
                  reason: 'Curated mock Grafana evidence for the incident subtype.',
                  widgets: [
                    {
                      id: 'incident-device-memory-p95-detail',
                      type: 'grafana_panel_iframe',
                      title: 'Primary evidence: incident device memory p95 5m',
                      bindings: {
                        vars: {
                          site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                          target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                        }
                      },
                      iframeUrl:
                        '/mock-grafana-iframe.html?panelId=21&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                    },
                    {
                      id: 'incident-device-cpu-p95-detail',
                      type: 'grafana_panel_iframe',
                      title: 'Secondary context: incident device CPU p95 5m',
                      bindings: {
                        vars: {
                          site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                          target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                        }
                      },
                      iframeUrl:
                        '/mock-grafana-iframe.html?panelId=20&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                    }
                  ]
                }
              }
            : incidentId === 'inc-007'
              ? {
                  id: incidentId,
                  title: 'Tunnel connectivity degraded',
                  subtype: 'site_tunnel_connectivity_degraded',
                  status: 'open',
                  severity: 'critical',
                  scopeType: 'site',
                  scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                  targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                  instance: 'tunnel.12',
                  createdAt: new Date(Date.now() - 28 * 60 * 1000).toISOString(),
                  updatedAt: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
                  description: 'Mock incident detail. In production this will include evidence summary and references.',
                  analysis: {
                    summary: 'REAP detected sustained tunnel connectivity degradation on Border1.example.com for instance tunnel.12.',
                    impact: 'Loss of an intended tunnel instance can remove overlay connectivity and reduce site-edge redundancy.',
                    rationale: [
                      'Incident subtype site_tunnel_connectivity_degraded indicates a specific intended-up tunnel instance remained operationally down beyond debounce.',
                      'The incident stays anchored on the owning device while preserving tunnel identity through the instance field.',
                      'Telemetry evidence focuses on tunnel operational state compared with intended admin state for the affected device.'
                    ],
                    nextChecks: [
                      'Confirm whether this tunnel instance is expected to be in service or was intentionally disabled.',
                      'Review recent VPN, IKE, or tunnel-interface logs on the same device around the open time.',
                      'Check whether peer-side reachability or a broader site-edge issue explains the tunnel outage.'
                    ]
                  },
                  topologyIntent: {
                    viewId: 'physical_overview',
                    scopeType: 'site',
                    scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                    limits: {
                      rootNodeId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                      hops: 1,
                      limitNodes: 40,
                      limitEdges: 64
                    },
                    reason: 'Center on the affected device in the physical topology.'
                  },
                  telemetryIntent: {
                    reason: 'Curated mock Grafana evidence for the incident subtype.',
                    widgets: [
                      {
                        id: 'incident-tunnel-state-detail',
                        type: 'grafana_panel_iframe',
                        title: 'Primary evidence: incident tunnel state',
                        bindings: {
                          vars: {
                            site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                            target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                          }
                        },
                        iframeUrl:
                          '/mock-grafana-iframe.html?panelId=22&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                      }
                    ]
                  }
                }
              : incidentId === 'inc-008'
                ? {
                    id: incidentId,
                    title: 'BFD liveness degraded',
                    subtype: 'site_bfd_liveness_degraded',
                    status: 'open',
                    severity: 'warning',
                    scopeType: 'site',
                    scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                    targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                    instance: 'peer:203.0.113.9',
                    createdAt: new Date(Date.now() - 21 * 60 * 1000).toISOString(),
                    updatedAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
                    description: 'Mock incident detail. In production this will include evidence summary and references.',
                    analysis: {
                      summary: 'REAP detected sustained BFD liveness degradation on Border1.example.com for session peer:203.0.113.9.',
                      impact: 'BFD instability can destabilize routed adjacencies and increase the risk of path churn or failover.',
                      rationale: [
                        'Incident subtype site_bfd_liveness_degraded indicates a specific BFD session stayed down or transitioned often enough to cross the instability threshold.',
                        'The incident stays anchored on the owning device while preserving session identity through the instance field.',
                        'Telemetry evidence focuses on current BFD session state and recent transition churn for the affected device.'
                      ],
                      nextChecks: [
                        'Check whether the session is currently down or repeatedly flapping over the recent rolling window.',
                        'Review device logs for BFD timeout, neighbor loss, or repeated liveness transitions tied to the same peer.',
                        'Verify underlying transport or adjacent interface health before treating the symptom as an isolated protocol issue.'
                      ]
                    },
                    topologyIntent: {
                      viewId: 'physical_overview',
                      scopeType: 'site',
                      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                      limits: {
                        rootNodeId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                        hops: 1,
                        limitNodes: 40,
                        limitEdges: 64
                      },
                      reason: 'Center on the affected device in the physical topology.'
                    },
                    telemetryIntent: {
                      reason: 'Curated mock Grafana evidence for the incident subtype.',
                      widgets: [
                        {
                          id: 'incident-bfd-session-state-detail',
                          type: 'grafana_panel_iframe',
                          title: 'Primary evidence: incident BFD session state',
                          bindings: {
                            vars: {
                              site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                              target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                            }
                          },
                          iframeUrl:
                            '/mock-grafana-iframe.html?panelId=23&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                        },
                        {
                          id: 'incident-bfd-transition-churn-detail',
                          type: 'grafana_panel_iframe',
                          title: 'Secondary context: incident BFD transition churn 15m',
                          bindings: {
                            vars: {
                              site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                              target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                            }
                          },
                          iframeUrl:
                            '/mock-grafana-iframe.html?panelId=24&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                        }
                      ]
                    }
                  }
                : incidentId === 'inc-009'
                  ? {
                      id: incidentId,
                      title: 'Device environmental risk',
                      subtype: 'device_environmental_risk',
                      status: 'open',
                      severity: 'warning',
                      scopeType: 'site',
                      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                      targetId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                      createdAt: new Date(Date.now() - 17 * 60 * 1000).toISOString(),
                      updatedAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
                      description: 'Mock incident detail. In production this will include evidence summary and references.',
                      analysis: {
                        summary: 'REAP detected sustained overheating risk on Border1.example.com.',
                        impact:
                          'Sustained environmental degradation raises device failure risk, can reduce hardware redundancy, and may precede service-impacting outages if thermal or power conditions worsen.',
                        rationale: [
                          'Incident subtype device_environmental_risk indicates overheating, cooling degradation, or PSU redundancy loss persisted long enough to surface as a durable device-level incident.',
                          'Topology context remains device-centered because the symptom is environmental hardware risk on one node rather than an interface-specific fault.',
                          'Telemetry evidence focuses on max temperature, failed fan count, and PSU redundancy ratio for the affected device.'
                        ],
                        nextChecks: [
                          'Check whether the dominant trigger is thermal rise, fan failure, or PSU redundancy loss before acting.',
                          'Review recent device logs for thermal alarms, fan failures, or power-supply state changes on the same node.',
                          'Confirm whether nearby rack power or airflow conditions could explain the environmental degradation.'
                        ]
                      },
                      topologyIntent: {
                        viewId: 'physical_overview',
                        scopeType: 'site',
                        scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                        limits: {
                          rootNodeId: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081',
                          hops: 1,
                          limitNodes: 40,
                          limitEdges: 64
                        },
                        reason: 'Center on the affected device in the physical topology.'
                      },
                      telemetryIntent: {
                        reason: 'Curated mock Grafana evidence for the incident subtype.',
                        widgets: [
                          {
                            id: 'incident-environment-max-temp-detail',
                            type: 'grafana_panel_iframe',
                            title: 'Primary evidence: incident environmental max temperature',
                            bindings: {
                              vars: {
                                site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                                target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                              }
                            },
                            iframeUrl:
                              '/mock-grafana-iframe.html?panelId=25&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                          },
                          {
                            id: 'incident-environment-failed-fan-count-detail',
                            type: 'grafana_panel_iframe',
                            title: 'Secondary context: incident failed fan count',
                            bindings: {
                              vars: {
                                site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                                target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                              }
                            },
                            iframeUrl:
                              '/mock-grafana-iframe.html?panelId=26&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                          },
                          {
                            id: 'incident-environment-psu-ratio-detail',
                            type: 'grafana_panel_iframe',
                            title: 'Secondary context: incident PSU redundancy ratio',
                            bindings: {
                              vars: {
                                site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                                target_id: 'ref:inventory.device:b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
                              }
                            },
                            iframeUrl:
                              '/mock-grafana-iframe.html?panelId=27&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.device%3Ab5c3eac8-6be6-4f6b-9c03-14f19a1f5081&from=now-1h&to=now'
                          }
                        ]
                      }
                    }
                : {
              id: incidentId,
              title: 'Interface congested (TopK)',
              subtype: 'interface_chronic_congestion',
              status: 'open',
              severity: 'warning',
              scopeType: 'site',
              scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
              targetId: 'ref:inventory.interface:740029df-3057-440b-9cba-4e3bfe96ce56#Te1/0/3',
              createdAt: new Date(Date.now() - 42 * 60 * 1000).toISOString(),
              updatedAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
              description: 'Mock incident detail. In production this will include evidence summary and references.',
              analysis: {
                summary: 'REAP detected sustained congestion on Te1/0/3 @ Dist1.example.com.',
                impact: 'Sustained congestion can reduce headroom, increase latency, and raise the risk of packet loss.',
                rationale: [
                  'Incident subtype interface_chronic_congestion is raised when utilization remains elevated long enough to cross the chronic threshold.',
                  'Telemetry evidence is centered on the affected interface and the site top watched interfaces for comparison.'
                ],
                nextChecks: [
                  'Confirm whether the utilization is expected for the current traffic pattern.',
                  'Check for drops, queue pressure, or shaping on the affected interface and upstream path.'
                ]
              },
              topologyIntent: {
                viewId: 'physical_overview',
                scopeType: 'site',
                scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
                limits: {
                  rootNodeId: 'ref:inventory.device:740029df-3057-440b-9cba-4e3bfe96ce56',
                  hops: 1,
                  limitNodes: 40,
                  limitEdges: 64
                },
                reason: 'Center on the parent device and expand physical interfaces for incident context.'
              },
              telemetryIntent: {
                reason: 'Curated mock Grafana evidence for the incident subtype.',
                widgets: [
                  {
                    id: 'incident-interface-detail',
                    type: 'grafana_panel_iframe',
                    title: 'Primary evidence: incident interface utilization',
                    bindings: {
                      vars: {
                        site_id: 'cbff025f-e881-4394-b735-eeaaa906187f',
                        target_id: 'ref:inventory.interface:740029df-3057-440b-9cba-4e3bfe96ce56#Te1/0/3'
                      }
                    },
                    iframeUrl:
                      '/mock-grafana-iframe.html?panelId=13&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&var-target_id=ref%3Ainventory.interface%3A740029df-3057-440b-9cba-4e3bfe96ce56%23Te1%2F0%2F3&from=now-1h&to=now'
                  },
                  {
                    id: 'incident-interface-topk',
                    type: 'grafana_panel_iframe',
                    title: 'Secondary context: top watched interface utilization in site',
                    bindings: { vars: { site_id: 'cbff025f-e881-4394-b735-eeaaa906187f' } },
                    iframeUrl: '/mock-grafana-iframe.html?panelId=11&var-site_id=cbff025f-e881-4394-b735-eeaaa906187f&from=now-1h&to=now'
                  }
                ]
              }
            }
  );
};

const handleIncidentTimeline: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgSignalsPath(url);
  if (!match) {
    return null;
  }

  const m = /^incidents\/([^/]+)\/timeline$/.exec(match.tail);
  if (!m) {
    return null;
  }
  const incidentId = decodeURIComponent(m[1] ?? '');

  const now = Date.now();
  return respond({
    incidentId,
    from: new Date(now - 60 * 60 * 1000).toISOString(),
    to: new Date(now).toISOString(),
    items:
      incidentId === 'inc-004'
        ? [
            {
              type: 'l2_transition',
              ts: new Date(now - 18 * 60 * 1000).toISOString(),
              conditionKey: 'bgp.neighbor_down',
              oldState: 'OFF',
              newState: 'ON',
              attributes: { source: 'mock', instance: '203.0.113.1' }
            },
            {
              type: 'l3_event',
              ts: new Date(now - 17 * 60 * 1000).toISOString(),
              eventType: 'opened',
              attributes: { source: 'mock' }
            },
            {
              type: 'l3_event',
              ts: new Date(now - 2 * 60 * 1000).toISOString(),
              eventType: 'updated',
              attributes: { source: 'mock', note: 'Peer state telemetry attached' }
            }
          ]
        : incidentId === 'inc-005'
          ? [
              {
                type: 'l2_transition',
                ts: new Date(now - 35 * 60 * 1000).toISOString(),
                conditionKey: 'bgp.unstable',
                oldState: 'OFF',
                newState: 'ON',
                attributes: { source: 'mock' }
              },
              {
                type: 'l3_event',
                ts: new Date(now - 34 * 60 * 1000).toISOString(),
                eventType: 'opened',
                attributes: { source: 'mock' }
              },
              {
                type: 'l3_event',
                ts: new Date(now - 3 * 60 * 1000).toISOString(),
                eventType: 'updated',
                attributes: { source: 'mock', note: 'Reset-churn telemetry attached' }
              }
            ]
          : [
              {
                type: 'l2_transition',
                ts: new Date(now - 55 * 60 * 1000).toISOString(),
                conditionKey: 'lag.degraded',
                oldState: 'OFF',
                newState: 'ON',
                attributes: { source: 'mock' }
              },
              {
                type: 'l3_event',
                ts: new Date(now - 42 * 60 * 1000).toISOString(),
                eventType: 'opened',
                attributes: { source: 'mock' }
              },
              {
                type: 'l3_event',
                ts: new Date(now - 5 * 60 * 1000).toISOString(),
                eventType: 'updated',
                attributes: { source: 'mock', note: 'Evidence attached' }
              }
            ]
  });
};

const handleIncidentEvents: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgSignalsPath(url);
  if (!match) {
    return null;
  }

  const m = /^incidents\/([^/]+)\/events$/.exec(match.tail);
  if (!m) {
    return null;
  }
  const incidentId = decodeURIComponent(m[1] ?? '');
  const now = Date.now();
  return respond({
    incidentId,
    from: new Date(now - 60 * 60 * 1000).toISOString(),
    to: new Date(now).toISOString(),
    groups:
      incidentId === 'inc-004'
        ? [
            {
              ts: new Date(now - 19 * 60 * 1000).toISOString(),
              summary: 'Related BGP peer events for 203.0.113.1 on Border1.example.com',
              severity: 'crit',
              deviceName: 'Border1.example.com',
              matchReason: 'Matched peer address; Matched incident-specific syslog keyword',
              events: [
                {
                  ts: new Date(now - 19 * 60 * 1000).toISOString(),
                  source: 'Device Syslog',
                  severity: 'warn',
                  summary: 'BGP neighbor 203.0.113.1 moved from Established to Active.',
                  deviceName: 'Border1.example.com',
                  message: 'BGP neighbor 203.0.113.1 moved from Established to Active.',
                  matchReason: 'Matched affected peer address'
                },
                {
                  ts: new Date(now - 18 * 60 * 1000).toISOString(),
                  source: 'Device Syslog',
                  severity: 'warn',
                  summary: 'BGP notification sent to 203.0.113.1 due to hold timer expiry.',
                  deviceName: 'Border1.example.com',
                  message: 'BGP notification sent to 203.0.113.1 due to hold timer expiry.',
                  matchReason: 'Matched incident-specific syslog keyword'
                }
              ]
            }
          ]
        : incidentId === 'inc-005'
          ? [
              {
                ts: new Date(now - 30 * 60 * 1000).toISOString(),
                summary: 'Related BGP churn events on Border1.example.com',
                severity: 'warn',
                deviceName: 'Border1.example.com',
                matchReason: 'Matched incident-specific syslog keyword',
                events: [
                  {
                    ts: new Date(now - 30 * 60 * 1000).toISOString(),
                    source: 'Device Syslog',
                    severity: 'warn',
                    summary: 'Multiple BGP peers reset on Border1.example.com during the last 15 minutes.',
                    deviceName: 'Border1.example.com',
                    message: 'Multiple BGP peers reset on Border1.example.com during the last 15 minutes.',
                    matchReason: 'Matched incident-specific syslog keyword'
                  },
                  {
                    ts: new Date(now - 28 * 60 * 1000).toISOString(),
                    source: 'Device Syslog',
                    severity: 'info',
                    summary: 'BGP FSM returned to Established for peer 198.51.100.2.',
                    deviceName: 'Border1.example.com',
                    message: 'BGP FSM returned to Established for peer 198.51.100.2.',
                    matchReason: 'Matched incident-specific syslog keyword'
                  }
                ]
              }
            ]
          : [
              {
                ts: new Date(now - 44 * 60 * 1000).toISOString(),
                summary: 'Related device syslogs for bundle member Te1/0/3 on Dist1.example.com',
                severity: 'warn',
                deviceName: 'Dist1.example.com',
                matchReason: 'Matched affected bundle member interface; Matched incident-specific syslog keyword',
                events: [
                  {
                    ts: new Date(now - 44 * 60 * 1000).toISOString(),
                    source: 'Device Syslog',
                    severity: 'warn',
                    summary: 'Port-channel Po10 member Te1/0/3 transitioned to down state.',
                    deviceName: 'Dist1.example.com',
                    message: 'Port-channel Po10 member Te1/0/3 transitioned to down state.',
                    matchReason: 'Matched affected bundle/interface name'
                  },
                  {
                    ts: new Date(now - 43 * 60 * 1000).toISOString(),
                    source: 'Device Syslog',
                    severity: 'info',
                    summary: 'LACP removed Te1/0/3 from Port-channel Po10.',
                    deviceName: 'Dist1.example.com',
                    message: 'LACP removed Te1/0/3 from Port-channel Po10 after partner timeout.',
                    matchReason: 'Matched incident-specific syslog keyword'
                  }
                ]
              }
            ],
    items:
      incidentId === 'inc-004'
        ? [
            {
              ts: new Date(now - 19 * 60 * 1000).toISOString(),
              source: 'Device Syslog',
              severity: 'warn',
              summary: 'BGP neighbor 203.0.113.1 moved from Established to Active.',
              deviceName: 'Border1.example.com',
              message: 'BGP neighbor 203.0.113.1 moved from Established to Active.',
              matchReason: 'Matched affected peer address'
            },
            {
              ts: new Date(now - 18 * 60 * 1000).toISOString(),
              source: 'Device Syslog',
              severity: 'warn',
              summary: 'BGP notification sent to 203.0.113.1 due to hold timer expiry.',
              deviceName: 'Border1.example.com',
              message: 'BGP notification sent to 203.0.113.1 due to hold timer expiry.',
              matchReason: 'Matched incident-specific syslog keyword'
            }
          ]
        : incidentId === 'inc-005'
          ? [
              {
                ts: new Date(now - 30 * 60 * 1000).toISOString(),
                source: 'Device Syslog',
                severity: 'warn',
                summary: 'Multiple BGP peers reset on Border1.example.com during the last 15 minutes.',
                deviceName: 'Border1.example.com',
                message: 'Multiple BGP peers reset on Border1.example.com during the last 15 minutes.',
                matchReason: 'Matched incident-specific syslog keyword'
              },
              {
                ts: new Date(now - 28 * 60 * 1000).toISOString(),
                source: 'Device Syslog',
                severity: 'info',
                summary: 'BGP FSM returned to Established for peer 198.51.100.2.',
                deviceName: 'Border1.example.com',
                message: 'BGP FSM returned to Established for peer 198.51.100.2.',
                matchReason: 'Matched incident-specific syslog keyword'
              }
            ]
          : [
              {
                ts: new Date(now - 44 * 60 * 1000).toISOString(),
                source: 'Device Syslog',
                severity: 'warn',
                summary: 'Port-channel Po10 member Te1/0/3 transitioned to down state.',
                deviceName: 'Dist1.example.com',
                message: 'Port-channel Po10 member Te1/0/3 transitioned to down state.',
                matchReason: 'Matched affected bundle/interface name'
              },
              {
                ts: new Date(now - 43 * 60 * 1000).toISOString(),
                source: 'Device Syslog',
                severity: 'info',
                summary: 'LACP removed Te1/0/3 from Port-channel Po10.',
                deviceName: 'Dist1.example.com',
                message: 'LACP removed Te1/0/3 from Port-channel Po10 after partner timeout.',
                matchReason: 'Matched incident-specific syslog keyword'
              }
            ]
  });
};

const handleSignals: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgSignalsPath(url);
  if (!match || match.tail !== '') {
    return null;
  }

  const scopeType = url.searchParams.get('scopeType') ?? 'site';
  const scopeId = url.searchParams.get('scopeId') ?? match.orgId;
  const family = url.searchParams.get('family');
  const deviceId = url.searchParams.get('deviceId');
  const signalKey = url.searchParams.get('signalKey');

  const items = SIGNAL_ROWS.filter((item) => (scopeType === 'site' ? item.scopeId === scopeId : true))
    .filter((item) => (family ? item.family === family : true))
    .filter((item) => (deviceId ? item.deviceId === deviceId : true))
    .filter((item) => (signalKey ? item.signalKey === signalKey : true))
    .map((item) => ({ ...item, orgId: match.orgId }));

  return respond({ items, meta: { limit: items.length, exactTotal: items.length } });
};

const handleFeatures: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgSignalsPath(url);
  if (!match || match.tail !== 'features') {
    return null;
  }

  const scopeType = url.searchParams.get('scopeType') ?? 'site';
  const scopeId = url.searchParams.get('scopeId') ?? match.orgId;
  const family = url.searchParams.get('family');
  const deviceId = url.searchParams.get('deviceId');
  const featureKey = url.searchParams.get('featureKey');

  const items = FEATURE_ROWS.filter((item) => (scopeType === 'site' ? item.scopeId === scopeId : true))
    .filter((item) => (family ? item.family === family : true))
    .filter((item) => (deviceId ? item.deviceId === deviceId : true))
    .filter((item) => (featureKey ? item.featureKey === featureKey : true))
    .map((item) => ({ ...item, orgId: match.orgId }));

  return respond({ items, meta: { limit: items.length, exactTotal: items.length } });
};

const handleConditions: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgSignalsPath(url);
  if (!match || match.tail !== 'conditions') {
    return null;
  }

  const scopeType = url.searchParams.get('scopeType') ?? 'site';
  const scopeId = url.searchParams.get('scopeId') ?? match.orgId;
  const family = url.searchParams.get('family');
  const deviceId = url.searchParams.get('deviceId');
  const conditionKey = url.searchParams.get('conditionKey');
  const state = (url.searchParams.get('state') ?? 'ON').trim().toUpperCase();

  const items = [
    {
      family: 'redundancy',
      deviceId: CORE_DEVICE_ID,
      conditionKey: 'lag.degraded',
      state: 'ON',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.interface:${CORE_DEVICE_ID}#Po10`,
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'Po10 @ core-sw-01' }
    },
    {
      family: 'interface',
      deviceId: CORE_DEVICE_ID,
      conditionKey: 'interface.congested',
      state: 'ON',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.interface:${CORE_DEVICE_ID}#Te1/0/3`,
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'Te1/0/3 @ core-sw-01' }
    },
    {
      family: 'interface',
      deviceId: 'a138914f-72cc-4cb2-b8c4-accb80d3f34a',
      conditionKey: 'interface.congested',
      state: 'ON',
      scopeType: 'site',
      scopeId: '60eaead0-1367-41c8-851b-ca34abd222d7',
      targetId: 'ref:inventory.interface:a138914f-72cc-4cb2-b8c4-accb80d3f34a#Te1/0/47',
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'Te1/0/47 @ Access1.example.com' }
    },
    {
      family: 'interface',
      deviceId: 'a138914f-72cc-4cb2-b8c4-accb80d3f34a',
      conditionKey: 'interface.erroring',
      state: 'ON',
      scopeType: 'site',
      scopeId: '60eaead0-1367-41c8-851b-ca34abd222d7',
      targetId: 'ref:inventory.interface:a138914f-72cc-4cb2-b8c4-accb80d3f34a#Gi1/0/24',
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'Gi1/0/24 @ Access1.example.com' }
    },
    {
      family: 'environment',
      deviceId: EDGE_DEVICE_ID,
      conditionKey: 'device.overheating',
      state: 'ON',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'edge-sw-01' }
    },
    {
      family: 'tunnel',
      deviceId: EDGE_DEVICE_ID,
      conditionKey: 'tunnel.down',
      state: 'ON',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
      instance: 'tunnel.12',
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'edge-sw-01' }
    },
    {
      family: 'bfd',
      deviceId: EDGE_DEVICE_ID,
      conditionKey: 'bfd.flapping',
      state: 'ON',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
      instance: 'peer:203.0.113.9',
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'edge-sw-01' }
    },
    {
      family: 'bgp',
      deviceId: EDGE_DEVICE_ID,
      conditionKey: 'bgp.neighbor_down',
      state: 'ON',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
      instance: '203.0.113.1',
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'edge-sw-01' }
    },
    {
      family: 'device_health',
      deviceId: EDGE_DEVICE_ID,
      conditionKey: 'device.memory_pressure',
      state: 'OFF',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'edge-sw-01' }
    },
    {
      family: 'tunnel',
      deviceId: EDGE_DEVICE_ID,
      conditionKey: 'tunnel.down',
      state: 'OFF',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
      instance: 'tunnel.99',
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'edge-sw-01' }
    },
    {
      family: 'device_health',
      deviceId: EDGE_DEVICE_ID,
      conditionKey: 'device.memory_pressure',
      state: 'ON',
      scopeType: 'site',
      scopeId: TELEMETRY_SITE_ID,
      targetId: `ref:inventory.device:${EDGE_DEVICE_ID}`,
      updatedAt: new Date().toISOString(),
      attributes: { targetLabel: 'edge-sw-01' }
    }
  ].filter((item) => {
    if (scopeType === 'site') {
      return item.scopeId === scopeId;
    }
    return true;
  }).filter((item) => (family ? item.family === family : true))
    .filter((item) => (state === 'ALL' ? true : item.state === state))
    .filter((item) => (deviceId ? item.deviceId === deviceId : true))
    .filter((item) => (conditionKey ? item.conditionKey === conditionKey : true))
    .map((item) => ({ ...item, orgId: match.orgId }));

  return respond({ items, meta: { limit: items.length, exactTotal: items.length } });
};

const handleWatchlist: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgSignalsPath(url);
  if (!match || match.tail !== 'watchlist') {
    return null;
  }

  const scopeType = url.searchParams.get('scopeType') ?? 'site';
  const scopeId = url.searchParams.get('scopeId') ?? match.orgId;
  const topic = url.searchParams.get('topic');
  const tier = url.searchParams.get('tier') ?? 'hot';

  const items = [
    {
      topicKey: 'traffic',
      tier,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      targetType: 'interface',
      targetId: 'ref:inventory.interface:53a39abd-243e-43b5-9cbf-e37ae6c407d7#Te1/0/2',
      updatedAt: new Date().toISOString(),
      attributes: {
        targetLabel: 'SFO uplink Te1/0/2',
        summary: 'Performance watch. Selected as a key bundle member on an inter-device capacity path.',
        explanation_details: ['Source: Performance Selection', 'Importance score: 90', 'Related targets: 1']
      }
    },
    {
      topicKey: 'uplinks',
      tier,
      scopeType: 'site',
      scopeId: 'cbff025f-e881-4394-b735-eeaaa906187f',
      targetType: 'interface',
      targetId: 'ref:inventory.interface:53a39abd-243e-43b5-9cbf-e37ae6c407d7#Po2',
      updatedAt: new Date().toISOString(),
      attributes: {
        targetLabel: 'SFO bundle Po2',
        summary: 'Redundancy watch. Selected as a LAG member on an inter-device redundancy path.',
        explanation_details: ['Source: Redundancy Selection', 'Importance score: 85', 'Related targets: 1']
      }
    },
    {
      topicKey: 'traffic',
      tier,
      scopeType: 'site',
      scopeId: '60eaead0-1367-41c8-851b-ca34abd222d7',
      targetType: 'interface',
      targetId: 'ref:inventory.interface:a138914f-72cc-4cb2-b8c4-accb80d3f34a#Te1/0/47',
      updatedAt: new Date().toISOString(),
      attributes: {
        targetLabel: 'NYC uplink Te1/0/47',
        summary: 'Performance watch. Selected because it carries an inter-device capacity path.',
        explanation_details: ['Source: Performance Selection', 'Importance score: 75']
      }
    },
    {
      topicKey: 'errors',
      tier,
      scopeType: 'site',
      scopeId: '60eaead0-1367-41c8-851b-ca34abd222d7',
      targetType: 'interface',
      targetId: 'ref:inventory.interface:a138914f-72cc-4cb2-b8c4-accb80d3f34a#Gi1/0/24',
      updatedAt: new Date().toISOString(),
      attributes: {
        targetLabel: 'NYC Gi1/0/24 CRC errors',
        summary: 'Availability watch. Selected by the baseline watchlist policy for this topic.',
        explanation_details: ['Source: Spec Baseline', 'Importance score: 60', 'Signals: Interface Error Rate 5m']
      }
    }
  ].filter((item) => {
    if (scopeType === 'site') {
      return item.scopeId === scopeId;
    }
    return true;
  }).filter((item) => (topic ? item.topicKey === topic : true));

  return respond({ items });
};

export const signalsHandlers: MockHandler[] = [handleSignals, handleFeatures, handleIncidents, handleIncidentDetail, handleIncidentTimeline, handleIncidentEvents, handleConditions, handleWatchlist];
